import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CORE_PERMS, CHAT_MODES, normalizeChatMode, MODULE_GROUPS, ALL_MODULE_KEYS, SOURCE_BADGE,
  SCHOOLS, buildInitialPerms, defaultPerms,
} from './permissionsData';
import { schoolPermissionsApi } from './api';

/* ═══════════════════════════════════════════════════════════════════
   SCHOOL PERMISSIONS — Super Admin module (frontend only)

   Control ERP access, core feature permissions, and module visibility
   per school. A stat strip + searchable/filterable table; "Manage" opens
   a modal with core toggles + grouped module toggles (All On / All Off,
   live active/inactive counts). Faithful port of the HTML design; all
   state is in-component (see ./permissionsData). No backend.
   ═══════════════════════════════════════════════════════════════════ */

export default function SchoolPermissions({ toast }) {
  const [schools, setSchools] = useState([]);
  const [permMap, setPermMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [erpFilter, setErpFilter] = useState('');     // '' | 'active' | 'inactive'
  const [editId, setEditId] = useState(null);          // school being managed

  /* Latest toast in a ref so the loader doesn't re-fire when the parent
     re-renders and hands down a fresh function (same trick as MentorAI). */
  const toastRef = useRef(toast);
  toastRef.current = toast;

  /* GET /api/SchoolPermissions/get-branch → table rows + the { id: perms }
     map the modal edits. Runs when the sidebar tab mounts this screen; the
     bundled demo data is only a fallback so the UI is never empty. */
  const loadSchools = useCallback(async () => {
    setLoading(true);
    try {
      const { schools: rows, permMap: perms } = await schoolPermissionsApi.listPermissionBranches();
      if (rows.length) {
        setSchools(rows);
        setPermMap(perms);
      } else {
        setSchools(SCHOOLS);
        setPermMap(buildInitialPerms());
        toastRef.current?.('No schools returned — showing sample data', 'warn');
      }
    } catch (err) {
      setSchools(SCHOOLS);
      setPermMap(buildInitialPerms());
      toastRef.current?.(err?.message || 'Could not load schools — showing sample data', 'warn');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSchools(); }, [loadSchools]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return schools.filter((s) => {
      const matches = !q || s.name.toLowerCase().includes(q) || (s.principal || '').toLowerCase().includes(q) || (s.contact || '').includes(q);
      if (!matches) return false;
      const on = permMap[s.id]?.erpAccess;
      if (erpFilter === 'active' && !on) return false;
      if (erpFilter === 'inactive' && on) return false;
      return true;
    });
  }, [schools, search, erpFilter, permMap]);

  const totalAll = schools.length;
  const activeAll = schools.filter((s) => permMap[s.id]?.erpAccess).length;

  /* Save Permissions → modules + ERP Access + Active Branch +
     POST /manage-mobileapp-permission (chatType + toggles). */
  const savePerms = async (id, perms) => {
    const s = schools.find((x) => x.id === id);
    setSaving(true);
    try {
      await schoolPermissionsApi.savePermissions(id, perms);
      setPermMap((prev) => ({ ...prev, [id]: perms }));
      setEditId(null);
      toast?.(`Permissions saved for ${s ? s.name : 'school'}`, 'success');
      /* Re-read so the table shows what the API actually stored. */
      loadSchools();
    } catch (err) {
      toast?.(err?.message || 'Could not save permissions', 'error');
    } finally {
      setSaving(false);
    }
  };

  const editSchool = editId != null ? schools.find((s) => s.id === editId) : null;

  return (
    <div className="page-content">
      {/* PAGE HEADER */}
      <div className="page-header">
        <div className="page-title-row">
          <div className="page-icon"><i className="fa-solid fa-key" /></div>
          <div>
            <div className="page-title">School Permissions</div>
            <div className="page-sub">Control ERP access, feature permissions, and module visibility for each school.</div>
          </div>
        </div>
      </div>

      {/* STATS */}
      <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(3,1fr)' }}>
        <div className="stat-card"><div className="stat-icon"><i className="fa-solid fa-school" /></div><div className="stat-val">{totalAll}</div><div className="stat-lbl">Total Schools</div></div>
        <div className="stat-card s-green"><div className="stat-icon"><i className="fa-solid fa-circle-check" /></div><div className="stat-val">{activeAll}</div><div className="stat-lbl">ERP Active</div></div>
        <div className="stat-card s-warn"><div className="stat-icon"><i className="fa-solid fa-ban" /></div><div className="stat-val">{totalAll - activeAll}</div><div className="stat-lbl">ERP Inactive</div></div>
      </div>

      {/* TABLE CARD */}
      <div className="section-card">
        <div className="sp-search-bar">
          <div className="f-field-grow">
            <div className="search-box">
              <i className="fa-solid fa-magnifying-glass" />
              <input className="search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by school name, owner, or contact…" />
            </div>
          </div>
          <select className="f-input" value={erpFilter} onChange={(e) => setErpFilter(e.target.value)} style={{ width: 160, height: 38 }}>
            <option value="">All ERP Status</option>
            <option value="active">ERP Active</option>
            <option value="inactive">ERP Inactive</option>
          </select>
          <button className="btn-sm" style={{ height: 38 }} onClick={loadSchools} disabled={loading} data-tip="Reload from API">
            <i className={`fa-solid fa-rotate${loading ? ' fa-spin' : ''}`} /> Refresh
          </button>
        </div>

        <div className="tbl-wrap">
          <table className="sp-table">
            <thead>
              <tr>
                <th style={{ width: 44 }}>#</th>
                <th>School Name</th>
                <th style={{ width: 90 }}>Code / ID</th>
                <th>Owner &amp; Contact</th>
                <th style={{ width: 110, textAlign: 'center' }}>Session</th>
                <th style={{ width: 120, textAlign: 'center' }}>ERP Status</th>
                <th style={{ width: 130, textAlign: 'center' }}>Permissions</th>
                <th style={{ width: 140, textAlign: 'center' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 44, color: 'var(--tm)' }}>
                  <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: 24, display: 'block', margin: '0 auto 12px', opacity: 0.5 }} />
                  <div style={{ fontSize: 14, fontWeight: 700 }}>Loading schools…</div>
                </td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 44, color: 'var(--tm)' }}>
                  <i className="fa-solid fa-magnifying-glass" style={{ fontSize: 28, display: 'block', margin: '0 auto 12px', opacity: 0.3 }} />
                  <div style={{ fontSize: 14, fontWeight: 700 }}>No schools found</div>
                </td></tr>
              ) : filtered.map((s, idx) => {
                const perms = permMap[s.id] || { modules: {} };
                const erpOn = perms.erpAccess;
                const modCount = ALL_MODULE_KEYS.filter((k) => perms.modules?.[k]).length;
                const src = SOURCE_BADGE[s.source] || SOURCE_BADGE.launch;
                return (
                  <tr key={s.id}>
                    <td className="td-bold" style={{ color: 'var(--tm)' }}>{idx + 1}</td>
                    <td>
                      {/* Hover par branch id — wahi id jo har API (module
                          permissions, toggle-launch-setup, school progress)
                          maangti hai, is liye testing me kaam aati hai. */}
                      <div style={{ fontWeight: 700, color: 'var(--t1)', fontSize: 13 }} title={`Branch ID: ${s.id}`}>
                        {s.name}
                      </div>
                      {s.address && (
                        <div style={{ fontSize: 11, color: 'var(--tm)', marginTop: 2 }} title={s.address}>
                          <i className="fa-solid fa-location-dot" style={{ marginRight: 4, opacity: 0.6 }} />{s.address}
                        </div>
                      )}
                      <div style={{ fontSize: 11, color: 'var(--tm)', marginTop: 2 }}><span className={`badge ${src.cls}`} style={{ fontSize: 9.5 }}>{src.label}</span></div>
                    </td>
                    <td><span style={{ fontSize: 12, fontWeight: 700, color: 'var(--brand)' }}>{s.schoolCode}</span></td>
                    <td>
                      <div style={{ fontWeight: 600, color: 'var(--t1)', fontSize: 12.5 }}>{s.principal || '—'}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--tm)' }}>{s.contact || '—'}</div>
                      {s.email && <div style={{ fontSize: 11, color: 'var(--tm)' }}>{s.email}</div>}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--t1)' }}>{s.session || '—'}</span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {erpOn
                        ? <span className="badge b-green"><i className="fa-solid fa-circle-check" style={{ fontSize: 8 }} /> Active</span>
                        : <span className="badge b-red"><i className="fa-solid fa-ban" style={{ fontSize: 8 }} /> Inactive</span>}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--brand)' }}>{modCount}/{ALL_MODULE_KEYS.length}</div>
                      <div style={{ fontSize: 10, color: 'var(--tm)' }}>{s.hasPermissionRow === false ? 'not set yet' : 'modules on'}</div>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button className="btn-sm" style={{ height: 30, fontSize: 11.5 }} onClick={() => setEditId(s.id)}>
                        <i className="fa-solid fa-sliders" /> Manage
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {editSchool && (
        <PermModal
          school={editSchool}
          initial={permMap[editSchool.id] || defaultPerms(editSchool)}
          saving={saving}
          toast={toast}
          onClose={() => setEditId(null)}
          onSave={(perms) => savePerms(editSchool.id, perms)}
        />
      )}
    </div>
  );
}

/* ═══════════════════════ TOGGLE SWITCH ═══════════════════════ */
export function Switch({ checked, onChange, disabled }) {
  return (
    <label className={`sw${disabled ? ' sw-disabled' : ''}`}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled} />
      <div className="sw-track" />
      <div className="sw-thumb" />
    </label>
  );
}

/* ═══════════════════════ PERMISSIONS MODAL ═══════════════════════ */
function PermModal({ school, initial, saving, toast, onClose, onSave }) {
  const [draft, setDraft] = useState(() => ({
    erpAccess: initial.erpAccess,
    activeBranch: initial.activeBranch,
    chatMode: normalizeChatMode(initial.chatMode),
    mentorAi: { ...(initial.mentorAi || { enabled: false, parentsAccess: false }) },
    etube: { ...(initial.etube || { enabled: false, viewing: false, uploading: false }) },
    mobileAppId: initial.mobileAppId || 0,
    modules: { ...initial.modules },
  }));
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [mobileLoading, setMobileLoading] = useState(true);
  const [mobileSaving, setMobileSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    setMobileLoading(true);
    schoolPermissionsApi.listMobileAppPermission(school.id)
      .then((mob) => {
        if (!alive) return;
        setDraft((d) => ({
          ...d,
          mobileAppId: mob.mobileAppId,
          /* Purani chaar-mode values (staffOnly / … ) bhi 'on' par aa jati hain. */
          chatMode: normalizeChatMode(mob.chatMode),
          mentorAi: { ...mob.mentorAi },
          etube: { ...mob.etube },
        }));
      })
      .catch(() => { /* defaults already in draft */ })
      .finally(() => { if (alive) setMobileLoading(false); });
    return () => { alive = false; };
  }, [school.id]);

  const setCore = (key, val) => setDraft((d) => ({ ...d, [key]: val }));
  const setModule = (key, val) => setDraft((d) => ({ ...d, modules: { ...d.modules, [key]: val } }));
  const setAll = (val) => setDraft((d) => ({ ...d, modules: Object.fromEntries(ALL_MODULE_KEYS.map((k) => [k, val])) }));
  const setMentorAi = (key, val) => setDraft((d) => {
    const next = { ...d.mentorAi, [key]: val };
    if (key === 'enabled' && !val) next.parentsAccess = false; // disabled → nobody can access it
    return { ...d, mentorAi: next };
  });
  const setEtube = (key, val) => setDraft((d) => {
    const next = { ...d.etube, [key]: val };
    if (key === 'enabled' && !val) { next.viewing = false; next.uploading = false; }
    return { ...d, etube: next };
  });

  const saveMobileAndClose = async () => {
    if (mobileSaving || mobileLoading) return;
    setMobileSaving(true);
    try {
      const saved = await schoolPermissionsApi.saveMobileAppPermission(school.id, draft);
      setDraft((d) => ({ ...d, mobileAppId: saved.mobileAppId }));
      setShowMobileModal(false);
      toast?.('Mobile app permissions saved', 'success');
    } catch (err) {
      toast?.(err?.message || 'Could not save mobile app permissions', 'error');
    } finally {
      setMobileSaving(false);
    }
  };

  const activeCount = ALL_MODULE_KEYS.filter((k) => draft.modules[k]).length;
  const inactiveCount = ALL_MODULE_KEYS.length - activeCount;

  return (
    <div className="perm-ov open" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="perm-modal">
        {/* Header */}
        <div className="pm-hdr">
          <div className="pm-av">{school.initials}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="pm-school-name" title={`Branch ID: ${school.id}`}>{school.name}</div>
            <div className="pm-school-meta">
              <span><i className="fa-solid fa-hashtag" style={{ color: 'var(--brand)' }} />{school.schoolCode}</span>
              <span><i className="fa-solid fa-user" style={{ color: 'var(--brand)' }} />{school.principal || '—'}</span>
              <span><i className="fa-solid fa-phone" style={{ color: 'var(--brand)' }} />{school.contact || '—'}</span>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
            <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--brand)' }}><i className="fa-solid fa-key" /> School Permissions</div>
            <button className="pm-close" data-tip="Close" data-tip-pos="left" onClick={onClose}><i className="fa-solid fa-xmark" /></button>
          </div>
        </div>

        {/* Body */}
        <div className="pm-body">
          {/* Core permissions */}
          <div className="pm-top-section">
            <div className="pm-top-title"><i className="fa-solid fa-shield-halved" /> Core Permissions</div>
            <div className="pm-top-grid">
              {CORE_PERMS.map((p) => (
                <div className={`pm-top-card${draft[p.key] ? ' enabled' : ''}`} key={p.key}>
                  <div className="pm-top-card-top">
                    <div className="pm-top-card-icon"><i className={`fa-solid ${p.icon}`} /></div>
                    <Switch checked={draft[p.key]} onChange={(v) => setCore(p.key, v)} />
                  </div>
                  <div className="pm-top-card-name">{p.name}</div>
                  <div className="pm-top-card-desc">{p.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile App permissions — nested modal. Save par
              POST /manage-mobileapp-permission (chatType + toggles). */}
          <div className="pm-top-section">
            <div className="pm-top-title"><i className="fa-solid fa-mobile-screen-button" /> Mobile App</div>
            <div
              className="pm-manage-card"
              style={mobileLoading ? { opacity: 0.6, pointerEvents: 'none' } : undefined}
              onClick={() => { if (!mobileLoading) setShowMobileModal(true); }}
            >
              <div className="pm-top-card-icon"><i className="fa-solid fa-sliders" /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="pm-top-card-name">Manage Mobile App Permissions</div>
                <div className="pm-top-card-desc">Control eTube, Mentor AI and Chat access for this school&rsquo;s mobile application.</div>
              </div>
              <i className="fa-solid fa-chevron-right pm-manage-arrow" />
            </div>
          </div>

          {/* Module permissions */}
          <div>
            <div className="pm-modules-title">
              <i className="fa-solid fa-table-cells-large" /> Module Permissions
              <div className="pm-mod-badges">
                <span className="badge b-green">{activeCount} Active</span>
                <span className="badge b-gray">{inactiveCount} Inactive</span>
              </div>
              <button className="btn-sm" style={{ marginLeft: 8, height: 28, fontSize: 11 }} onClick={() => setAll(true)}><i className="fa-solid fa-toggle-on" /> All On</button>
              <button className="btn-sm" style={{ height: 28, fontSize: 11, borderColor: 'var(--err)', color: 'var(--err)', background: 'rgba(220,38,38,.05)' }} onClick={() => setAll(false)}><i className="fa-solid fa-toggle-off" /> All Off</button>
            </div>

            {MODULE_GROUPS.map((g) => (
              <React.Fragment key={g.label}>
                <div className="pm-section-label">{g.label}</div>
                <div className="pm-mod-grid">
                  {g.modules.map((m) => (
                    <div className={`pm-mod-card${draft.modules[m.key] ? ' enabled' : ''}`} key={m.key}>
                      <div className="pm-mod-icon"><i className={`fa-solid ${m.icon}`} /></div>
                      <div className="pm-mod-name">{m.name}</div>
                      <Switch checked={draft.modules[m.key]} onChange={(v) => setModule(m.key, v)} />
                    </div>
                  ))}
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="pm-foot">
          <button className="btn-secondary" onClick={onClose} disabled={saving}><i className="fa-solid fa-xmark" /> Cancel</button>
          <button className="btn-primary" onClick={() => onSave(draft)} disabled={saving || mobileLoading}>
            <i className={`fa-solid ${saving ? 'fa-spinner fa-spin' : 'fa-floppy-disk'}`} /> {saving ? 'Saving…' : 'Save Permissions'}
          </button>
        </div>
      </div>

      {showMobileModal && (
        <MobileAppPermsModal
          draft={draft}
          loading={mobileLoading}
          saving={mobileSaving}
          setCore={setCore}
          setMentorAi={setMentorAi}
          setEtube={setEtube}
          onClose={() => { if (!mobileSaving) setShowMobileModal(false); }}
          onDone={saveMobileAndClose}
        />
      )}
    </div>
  );
}

/* Nested modal — Chat / Mentor AI / eTube. Done → POST SAVE
   /manage-mobileapp-permission (chatType + toggles). Network Permissions
   bhi yahi modal istemal karti hai (`subject` se wording badalti hai). */
export function MobileAppPermsModal({ draft, loading, saving, setCore, setMentorAi, setEtube, onClose, onDone, subject = 'school' }) {
  return (
    <div className="perm-ov open" onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div className="perm-modal" style={{ maxWidth: 560 }}>
        <div className="pm-hdr">
          <div className="pm-av"><i className="fa-solid fa-mobile-screen-button" /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="pm-school-name">Manage Mobile App Permissions</div>
            <div className="pm-school-meta"><span>Chat, Mentor AI &amp; eTube access for this {subject}&rsquo;s mobile application</span></div>
          </div>
          <button className="pm-close" data-tip="Back" data-tip-pos="left" onClick={onClose} disabled={saving}><i className="fa-solid fa-xmark" /></button>
        </div>

        <div className="pm-body">
          {loading && (
            <div style={{ textAlign: 'center', padding: '8px 0 16px', color: 'var(--tm)', fontSize: 12.5, fontWeight: 700 }}>
              <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: 8 }} /> Loading saved mobile permissions…
            </div>
          )}
          {/* Chat */}
          <div className="pm-top-section">
            <div className="pm-top-title"><i className="fa-solid fa-comments" /> Chat</div>
            <div className="pm-chat-grid">
              {CHAT_MODES.map((m) => (
                <div
                  key={m.key}
                  className={`pm-chat-card${normalizeChatMode(draft.chatMode) === m.key ? ' selected' : ''}`}
                  onClick={() => setCore('chatMode', m.key)}
                >
                  <div className="pm-chat-radio" />
                  <div className="pm-chat-icon"><i className={`fa-solid ${m.icon}`} /></div>
                  <div className="pm-chat-body">
                    <div className="pm-chat-name">{m.name}</div>
                    <div className="pm-chat-desc">{m.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mentor AI */}
          <div className="pm-top-section">
            <div className="pm-top-title"><i className="fa-solid fa-robot" /> Mentor AI</div>
            <div className="pm-mob-card">
              <div className="pm-mob-row">
                <div className="pm-mob-row-l">
                  <div className="pm-mob-row-name">Enable Mentor AI</div>
                  <div className="pm-mob-row-desc">Turn Mentor AI on or off for this {subject}&rsquo;s mobile app. When off, nobody — staff or parents — can access it.</div>
                </div>
                <Switch checked={draft.mentorAi.enabled} onChange={(v) => setMentorAi('enabled', v)} />
              </div>
              <div className={`pm-mob-row${!draft.mentorAi.enabled ? ' pm-mob-row-disabled' : ''}`}>
                <div className="pm-mob-row-l">
                  <div className="pm-mob-row-name">Parents Access</div>
                  <div className="pm-mob-row-desc">
                    {draft.mentorAi.enabled
                      ? 'Let parents use Mentor AI too. When off, only staff can use it.'
                      : 'Enable Mentor AI above first — parents access has no effect while Mentor AI is disabled.'}
                  </div>
                </div>
                <Switch
                  checked={draft.mentorAi.enabled && draft.mentorAi.parentsAccess}
                  onChange={(v) => setMentorAi('parentsAccess', v)}
                  disabled={!draft.mentorAi.enabled}
                />
              </div>
            </div>
          </div>

          {/* eTube */}
          <div className="pm-top-section" style={{ marginBottom: 0 }}>
            <div className="pm-top-title"><i className="fa-solid fa-play-circle" /> eTube</div>
            <div className="pm-mob-card">
              <div className="pm-mob-row">
                <div className="pm-mob-row-l">
                  <div className="pm-mob-row-name">Enable eTube</div>
                  <div className="pm-mob-row-desc">Turn eTube on or off for this {subject}&rsquo;s mobile app.</div>
                </div>
                <Switch checked={draft.etube.enabled} onChange={(v) => setEtube('enabled', v)} />
              </div>
              <div className={`pm-mob-row${!draft.etube.enabled ? ' pm-mob-row-disabled' : ''}`}>
                <div className="pm-mob-row-l">
                  <div className="pm-mob-row-name">Video Viewing</div>
                  <div className="pm-mob-row-desc">Whether users can watch eTube videos.</div>
                </div>
                <Switch checked={draft.etube.enabled && draft.etube.viewing} onChange={(v) => setEtube('viewing', v)} disabled={!draft.etube.enabled} />
              </div>
              <div className={`pm-mob-row${!draft.etube.enabled ? ' pm-mob-row-disabled' : ''}`}>
                <div className="pm-mob-row-l">
                  <div className="pm-mob-row-name">Video Uploading</div>
                  <div className="pm-mob-row-desc">Whether {subject} users can upload videos to eTube.</div>
                </div>
                <Switch checked={draft.etube.enabled && draft.etube.uploading} onChange={(v) => setEtube('uploading', v)} disabled={!draft.etube.enabled} />
              </div>
            </div>
          </div>
        </div>

        <div className="pm-foot">
          <button className="btn-primary" onClick={onDone} disabled={saving || loading}>
            <i className={`fa-solid ${saving ? 'fa-spinner fa-spin' : 'fa-check'}`} /> {saving ? 'Saving…' : 'Done'}
          </button>
        </div>
      </div>
    </div>
  );
}
